import { useEffect } from 'react';
import { cn } from '../../styles/lib/utils';
import { renderLineHitRegion } from './Shapes/Line';
import { renderPointHitRegion } from './Shapes/Point';
import { renderPolygonHitRegion } from './Shapes/Polygon';
import {
  type DrawingCanvasColorToShapeKeyMap,
  type DrawingCanvasSelectedFeature,
  type DrawingCanvasShape,
  type DrawingCanvasShapeKey,
  type DrawingCanvasShapeKeyToColorMap,
  DrawingCanvasShapeType,
  type Point,
} from './types';
import { indexToColor } from './utils';

export type HitCanvasProps = {
  width: number;
  height: number;
  className?: string;
  ref: React.RefObject<HTMLCanvasElement | null>;
  sortedDrawingShapes: (DrawingCanvasShape | DrawingCanvasSelectedFeature)[];
  shapeKeyToColorMap: DrawingCanvasShapeKeyToColorMap;
  selectedShapeKey?: DrawingCanvasShapeKey;
};

export const generateColorShapeMaps = (
  drawingShapeKeys: DrawingCanvasShapeKey[],
): { colorsToKeys: DrawingCanvasColorToShapeKeyMap; keysToColors: DrawingCanvasShapeKeyToColorMap } => {
  const colorsToKeys: DrawingCanvasColorToShapeKeyMap = {};
  const keysToColors: DrawingCanvasShapeKeyToColorMap = {};
  drawingShapeKeys.forEach((key, index) => {
    const color = indexToColor(index);
    colorsToKeys[color] = key;
    keysToColors[key] = color;
  });

  return {
    colorsToKeys,
    keysToColors,
  };
};

export const getHitShapeKey = (
  hitPoint: Point,
  hitCanvasContext: CanvasRenderingContext2D,
  colorToShapeMap: DrawingCanvasColorToShapeKeyMap,
): DrawingCanvasShapeKey | undefined => {
  const pixel = hitCanvasContext.getImageData(hitPoint.x, hitPoint.y, 1, 1).data;
  const color = `rgb(${pixel[0]},${pixel[1]},${pixel[2]})`;
  return colorToShapeMap[color];
};

export const HitDetectionCanvas = ({
  width,
  height,
  className,
  ref,
  sortedDrawingShapes,
  shapeKeyToColorMap,
  selectedShapeKey,
}: HitCanvasProps) => {
  useEffect(() => {
    const ctx = ref.current?.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.reset();

    for (const shape of sortedDrawingShapes) {
      const color = shapeKeyToColorMap[shape.key];

      switch (shape.type) {
        case DrawingCanvasShapeType.LINE:
          renderLineHitRegion(shape.points, ctx, color, shape.style);
          break;
        case DrawingCanvasShapeType.POINT:
        case DrawingCanvasShapeType.BBOX_CORNER:
        case DrawingCanvasShapeType.VERTEX:
          if (shape.points.length === 0) break;
          renderPointHitRegion(shape.points[0], ctx, color, shape.style);
          break;
        case DrawingCanvasShapeType.POLYGON:
        case DrawingCanvasShapeType.BBOX:
          renderPolygonHitRegion(shape.points, ctx, color, shape.key !== selectedShapeKey, shape.style);
          break;
      }
    }
  }, [sortedDrawingShapes, shapeKeyToColorMap, ref, selectedShapeKey]);

  return (
    // biome-ignore lint/a11y/noAriaHiddenOnFocusable: a canvas without tabIndex isn't focusable; this one only holds hit-test colors
    <canvas
      ref={ref}
      aria-hidden="true"
      width={width}
      height={height}
      data-slot="drawing-canvas-hit"
      className={cn(className)}
    />
  );
};
